/**
 * What the door she came in by already told us (meta #321 P2-5): screens read
 * this instead of asking again, and her account gets it once she has one.
 */
import type { DiagnosticExamType } from '@/utils/diagnosticMemory';
import { JURISDICTIONS } from '@/features/licensing/jurisdictions';
import { getItemWithTTL, setItemWithTTL, TTL } from '@/utils/localStorageWithTTL';
import { detectSignupEntry } from '@/utils/signupRoad';

export interface LearnerContext {
  /** USPS code. A pre-fill she confirms, never written to her profile unasked. */
  jurisdiction?: string;
  /** ASWB school code. */
  schoolCode?: string;
  exam?: DiagnosticExamType;
  retake?: boolean;
}

type Field = keyof LearnerContext;

/** Higher = more specific. */
const DOOR_RANK = { ad: 1, program: 2, licensing: 3 } as const;
export type Door = keyof typeof DOOR_RANK;

interface Stored {
  ctx: LearnerContext;
  from: Partial<Record<Field, Door>>;
}

export const LEARNER_CONTEXT_KEY = 'swtpa_learner_context_v1';

function read(): Stored {
  const stored = getItemWithTTL<Stored>(LEARNER_CONTEXT_KEY);
  return stored?.ctx && stored.from ? stored : { ctx: {}, from: {} };
}

export function learnerContext(): LearnerContext {
  return read().ctx;
}

/** Empty values never erase; a less specific door never replaces a more specific one. */
export function rememberDoor(door: Door, found: LearnerContext): void {
  const { ctx, from } = read();
  let changed = false;
  for (const field of Object.keys(found) as Field[]) {
    const value = found[field];
    if (value === undefined || value === '') continue;
    const prior = from[field];
    if (prior && DOOR_RANK[prior] > DOOR_RANK[door]) continue;
    (ctx as Record<Field, unknown>)[field] = value;
    from[field] = door;
    changed = true;
  }
  if (changed) setItemWithTTL<Stored>(LEARNER_CONTEXT_KEY, { ctx, from }, TTL.THREE_MONTHS);
}

export function knownJurisdiction(code: string | null | undefined): string | undefined {
  const upper = code?.toUpperCase();
  return upper && JURISDICTIONS.some((j) => j.code === upper) ? upper : undefined;
}

const EXAM_WORDS: [DiagnosticExamType, RegExp][] = [
  ['LCSW', /\b(lcsw|clinical)\b/],
  ['LMSW', /\b(lmsw|masters?)\b/],
  ['LBSW', /\b(lbsw|bachelors?)\b/],
];

/** The exam level and retake signal in an ad click's search term / utm; an ambiguous level is left out. */
export function adContextFromSearch(search: string): LearnerContext {
  const p = new URLSearchParams(search);
  const text = ['utm_term', 'utm_campaign', 'utm_content']
    .map((k) => p.get(k) ?? '')
    .join(' ')
    .toLowerCase()
    .replace(/[^a-z]+/g, ' ');
  const exams = EXAM_WORDS.filter(([, re]) => re.test(text)).map(([exam]) => exam);
  return {
    ...(exams.length === 1 ? { exam: exams[0] } : {}),
    ...(/\b(retake|retaker|retaking|failed|repeat)\b/.test(text) ? { retake: true } : {}),
  };
}

/** App load: an ad click records what its search term says. */
export function rememberAdDoor(pathname: string, search: string): void {
  if (detectSignupEntry(pathname, search) === 'ad') rememberDoor('ad', adContextFromSearch(search));
}

/** The door's school for a profile with none. Never state: a school's state is not where she is licensing. */
export function contextProfileFill(profile: Record<string, unknown> | undefined): { schoolCode?: string } {
  const { schoolCode } = learnerContext();
  return schoolCode && !profile?.schoolCode ? { schoolCode } : {};
}
